import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
    BarChart3,
    Bell,
    Building2,
    ChevronDown,
    Code2,
    FolderKanban,
    LayoutDashboard,
    LogOut,
    Menu as MenuIcon,
    MessageSquareWarning,
    Palette,
    UserCircle,
    Users,
    X
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import type { NotificationModel } from '@/domain/models/notification.js';
import { BrandSignature, ThemeModeSelector } from '@/presentation/components/index.js';
import type { NotificationController } from '@/presentation/controllers/notification-controller.js';
import { useApp } from '@/presentation/hooks/use-app.js';
import { clearShowCanceledSession, clearStatusFilterSession } from '@/presentation/hooks/use-developments.js';
import { resolveVisibleNavItems } from '@/presentation/layouts/app-shell/utils/resolve-visible-nav-items.js';
import { BRAND_NAVY } from '@/shared/constants/brand.js';
import { ROUTES } from '@/shared/constants/routes.js';
import { formatRelative } from '@/shared/utils/format-date.js';

const DRAWER_WIDTH = 240;
const APPBAR_HEIGHT = 56;

interface NavItem {
    label: string;
    icon: React.ElementType;
    path: string;
    profiles: string[];
}

const NAV_ITEMS: NavItem[] = [
    { label: 'nav.dashboard', icon: LayoutDashboard, path: ROUTES.DASHBOARD, profiles: ['ADM', 'PMO', 'CF', 'GPA', 'GPC', 'LT', 'DEV', 'UC'] },
    { label: 'nav.developments', icon: Code2, path: ROUTES.DEVELOPMENTS, profiles: ['ADM', 'PMO', 'CF', 'GPA', 'GPC', 'LT', 'DEV', 'UC'] },
    { label: 'nav.clients', icon: Building2, path: ROUTES.CLIENTS, profiles: ['ADM', 'PMO'] },
    { label: 'nav.projects', icon: FolderKanban, path: ROUTES.PROJECTS, profiles: ['ADM', 'PMO'] },
    { label: 'nav.users', icon: Users, path: ROUTES.USERS, profiles: ['ADM', 'PMO'] },
    { label: 'nav.reasons', icon: MessageSquareWarning, path: ROUTES.REASONS, profiles: ['ADM'] },
    { label: 'nav.appearance', icon: Palette, path: ROUTES.APPEARANCE, profiles: ['ADM'] },
    { label: 'nav.reports', icon: BarChart3, path: ROUTES.REPORTS, profiles: ['ADM', 'GPA'] },
    { label: 'nav.notifications', icon: Bell, path: ROUTES.NOTIFICATIONS, profiles: ['ADM', 'PMO', 'CF', 'GPA', 'GPC', 'LT', 'DEV', 'UC'] }
];

interface NotificationBellProps {
    controller: NotificationController;
}

function NotificationBell({ controller }: NotificationBellProps) {
    const { translate } = useApp();
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [unreadCount, setUnreadCount] = useState(0);
    const [recent, setRecent] = useState<NotificationModel[]>([]);

    const fetchUnread = useCallback(async () => {
        const result = await controller.getUnreadCount();
        result.fold(
            () => {},
            (data) => setUnreadCount(data.count)
        );
    }, [controller]);

    const fetchRecent = useCallback(async () => {
        const result = await controller.loadNotifications({ page: 1, limit: 5 });
        result.fold(
            () => {},
            (data) => {
                setRecent(data.data);
                setUnreadCount(data.unreadCount);
            }
        );
    }, [controller]);

    useEffect(() => {
        fetchUnread();
        const interval = setInterval(fetchUnread, 30000);
        return () => clearInterval(interval);
    }, [fetchUnread]);

    const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(e.currentTarget);
        fetchRecent();
    };

    const handleClose = () => setAnchorEl(null);

    const handleMarkRead = async (id: string) => {
        const result = await controller.markRead(id);
        if (result.isRight()) {
            await fetchRecent();
        }
    };

    const handleViewAll = () => {
        handleClose();
        navigate(ROUTES.NOTIFICATIONS);
    };

    const open = Boolean(anchorEl);

    return (
        <>
            <IconButton onClick={handleOpen} size="small" sx={{ color: 'white' }}>
                <Badge badgeContent={unreadCount > 0 ? unreadCount : undefined} color="error">
                    <Bell size={18} />
                </Badge>
            </IconButton>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: { width: 320, maxHeight: 480 } }}
            >
                <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" fontWeight={600}>
                        {translate('nav.notifications')}
                        {unreadCount > 0 && <Chip label={unreadCount} size="small" color="error" sx={{ ml: 1, height: 18, fontSize: 11 }} />}
                    </Typography>
                </Box>
                {recent.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Bell size={20} style={{ opacity: 0.3, marginBottom: 8 }} />
                        <Typography variant="body2" color="text.secondary">
                            {translate('topBar.noNotifications')}
                        </Typography>
                    </Box>
                ) : (
                    <Box>
                        {recent.map((n) => (
                            <Box
                                key={n.id}
                                sx={{
                                    p: 1.5,
                                    borderBottom: 1,
                                    borderColor: 'divider',
                                    '&:last-of-type': { borderBottom: 0 },
                                    bgcolor: n.readAt ? 'transparent' : 'rgba(94, 74, 227, 0.04)'
                                }}
                            >
                                <Stack direction="row" spacing={1} alignItems="flex-start">
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', mt: 0.75, flexShrink: 0, bgcolor: n.readAt ? 'grey.500' : BRAND_NAVY }} />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="caption" fontWeight={600} noWrap>
                                            {n.title}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                        >
                                            {n.body}
                                        </Typography>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5 }}>
                                            <Typography variant="caption" color="text.disabled">
                                                {formatRelative(n.createdAt, translate)}
                                            </Typography>
                                            {!n.readAt && (
                                                <Button size="small" onClick={() => handleMarkRead(n.id)} sx={{ fontSize: 11, minWidth: 'auto', textTransform: 'none' }}>
                                                    {translate('topBar.read')}
                                                </Button>
                                            )}
                                        </Stack>
                                    </Box>
                                </Stack>
                            </Box>
                        ))}
                    </Box>
                )}
                <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider' }}>
                    <Button size="small" fullWidth onClick={handleViewAll} sx={{ textTransform: 'none' }}>
                        {translate('topBar.viewAllNotifications')}
                    </Button>
                </Box>
            </Popover>
        </>
    );
}

interface AppShellProps {
    notificationController: NotificationController;
}

export function AppShell({ notificationController }: AppShellProps) {
    const { translate, useAuthStore, useProjectStore, useThemeStore } = useApp();
    const user = useAuthStore((s) => s.user);
    const clearAuth = useAuthStore((s) => s.clearAuth);
    const projects = useProjectStore((s) => s.projects);
    const selectedProject = useProjectStore((s) => s.selectedProject);
    const setSelectedProject = useProjectStore((s) => s.setSelectedProject);
    const headerLogo = useThemeStore((s) => s.theme.logos.header);
    const resetToDefaultTheme = useThemeStore((s) => s.resetToDefaultTheme);

    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
    const primaryMain = muiTheme.palette.primary.main;
    const secondaryMain = muiTheme.palette.secondary.main;

    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [userMenu, setUserMenu] = useState<HTMLElement | null>(null);
    const [projectMenu, setProjectMenu] = useState<HTMLElement | null>(null);
    const navigate = useNavigate();
    const location = useLocation();

    const visibleItems = resolveVisibleNavItems(NAV_ITEMS, user);
    const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');

    useEffect(() => {
        if (!location.pathname.startsWith(ROUTES.DEVELOPMENTS)) {
            clearShowCanceledSession();
            clearStatusFilterSession();
        }
    }, [location.pathname]);
    const initials =
        user?.name
            ?.split(' ')
            .filter(Boolean)
            .slice(0, 2)
            .map((n: string) => n[0])
            .join('')
            .toUpperCase() ?? '?';

    const handleLogout = () => {
        clearAuth();
        resetToDefaultTheme();
        navigate(ROUTES.LOGIN);
        setUserMenu(null);
    };

    const handleNavigate = (path: string) => {
        navigate(path);
        setSidebarOpen(false);
    };

    const renderDrawerContent = () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: primaryMain }}>
            <List sx={{ flex: 1, px: 1, py: 1.5 }}>
                {visibleItems.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.path);
                    return (
                        <ListItemButton
                            key={item.path}
                            selected={active}
                            onClick={() => handleNavigate(item.path)}
                            sx={{
                                borderRadius: 1,
                                mb: 0.25,
                                py: 1,
                                minHeight: 40,
                                color: active ? 'white' : 'rgba(255,255,255,0.7)',
                                bgcolor: active ? 'rgba(255,255,255,0.15)' : 'transparent',
                                '&:hover': {
                                    color: 'white',
                                    bgcolor: 'rgba(255,255,255,0.1)'
                                },
                                '&.Mui-selected': {
                                    color: 'white',
                                    bgcolor: 'rgba(255,255,255,0.15)'
                                },
                                '&.Mui-selected:hover': {
                                    bgcolor: 'rgba(255,255,255,0.15)'
                                }
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: 40, justifyContent: 'center', color: 'inherit' }}>
                                <Icon size={18} />
                            </ListItemIcon>
                            <ListItemText primary={translate(item.label)} slotProps={{ primary: { fontSize: 13, fontWeight: 500 } }} />
                        </ListItemButton>
                    );
                })}
            </List>

            <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.1)', px: 1, py: 1.5 }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ px: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: secondaryMain, fontSize: 12, fontWeight: 600 }}>{initials}</Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" sx={{ color: 'white', fontWeight: 500, display: 'block' }} noWrap>
                            {user?.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block' }} noWrap>
                            {user ? translate(`profiles.${user.profile}`) : ''}
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={handleLogout} sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: 'white' } }}>
                        <LogOut size={16} />
                    </IconButton>
                </Stack>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <AppBar
                position="fixed"
                sx={{ zIndex: (th) => th.zIndex.drawer + 1, bgcolor: 'primary.main', color: 'white', boxShadow: 'none', borderBottom: '1px solid rgba(255,255,255,0.1)' }}
            >
                <Toolbar variant="dense" sx={{ height: APPBAR_HEIGHT, minHeight: APPBAR_HEIGHT, gap: 1 }}>
                    <IconButton edge="start" onClick={() => setSidebarOpen(!sidebarOpen)} sx={{ color: 'white' }}>
                        {sidebarOpen ? <X size={20} /> : <MenuIcon size={20} />}
                    </IconButton>
                    <BrandSignature variant="header" logoHeight={28} logoUrl={headerLogo || undefined} />
                    <Box sx={{ flex: 1 }} />
                    <Box>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={(e) => setProjectMenu(e.currentTarget)}
                            startIcon={<FolderKanban size={15} />}
                            endIcon={<ChevronDown size={13} />}
                            sx={{
                                textTransform: 'none',
                                borderColor: 'rgba(255,255,255,0.2)',
                                color: 'white',
                                '&:hover': { borderColor: 'rgba(255,255,255,0.4)', bgcolor: 'rgba(255,255,255,0.05)' }
                            }}
                        >
                            <Typography variant="caption" noWrap sx={{ maxWidth: 160 }}>
                                {selectedProject ? `${selectedProject.client.abbreviation} / ${selectedProject.name}` : translate('topBar.selectProject')}
                            </Typography>
                        </Button>
                        <Menu anchorEl={projectMenu} open={!!projectMenu} onClose={() => setProjectMenu(null)} PaperProps={{ sx: { width: 288, maxHeight: 320 } }}>
                            {selectedProject && (
                                <MenuItem
                                    onClick={() => {
                                        setSelectedProject(null);
                                        setProjectMenu(null);
                                    }}
                                    sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <X size={16} />
                                        <Typography variant="body2">{translate('topBar.clearSelection')}</Typography>
                                    </Box>
                                </MenuItem>
                            )}
                            {projects.length === 0 ? (
                                <MenuItem disabled>{translate('topBar.noProjectsAvailable')}</MenuItem>
                            ) : (
                                projects.map((p: (typeof projects)[number]) => (
                                    <MenuItem
                                        key={p.id}
                                        onClick={() => {
                                            setSelectedProject(p);
                                            setProjectMenu(null);
                                        }}
                                        selected={selectedProject?.id === p.id}
                                    >
                                        <Box>
                                            <Typography variant="body2" fontWeight={500}>
                                                {p.name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {p.client.name}
                                            </Typography>
                                        </Box>
                                    </MenuItem>
                                ))
                            )}
                        </Menu>
                    </Box>
                    <NotificationBell controller={notificationController} />
                    <Box>
                        <Button
                            size="small"
                            onClick={(e) => setUserMenu(e.currentTarget)}
                            startIcon={<Avatar sx={{ width: 32, height: 32, bgcolor: secondaryMain, fontSize: 12, fontWeight: 600 }}>{initials}</Avatar>}
                            endIcon={<ChevronDown size={14} />}
                            sx={{ textTransform: 'none', color: 'white', '& .MuiAvatar-root': { fontSize: '12px' } }}
                        >
                            <Box sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'left' }}>
                                <Typography variant="caption" noWrap sx={{ display: 'block', fontWeight: 500, color: 'white' }}>
                                    {user?.name}
                                </Typography>
                                <Typography variant="caption" noWrap sx={{ display: 'block', color: 'rgba(255,255,255,0.4)' }}>
                                    {user ? translate(`profiles.${user.profile}`) : ''}
                                </Typography>
                            </Box>
                        </Button>
                        <Menu anchorEl={userMenu} open={!!userMenu} onClose={() => setUserMenu(null)} PaperProps={{ sx: { width: 224 } }}>
                            <Box sx={{ px: 2, py: 1.5 }}>
                                <Typography variant="body2" fontWeight={600} noWrap>
                                    {user?.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" noWrap>
                                    {user?.email}
                                </Typography>
                                <Chip
                                    label={user ? translate(`profiles.${user.profile}`) : ''}
                                    size="small"
                                    sx={{ mt: 0.5, height: 20, fontSize: 11, bgcolor: 'action.hover', color: 'text.primary', fontWeight: 500 }}
                                />
                            </Box>
                            <Divider />
                            <MenuItem
                                onClick={() => {
                                    setUserMenu(null);
                                    navigate(ROUTES.PROFILE);
                                }}
                            >
                                <UserCircle size={15} style={{ color: 'text.secondary' }} />
                                <Box sx={{ ml: 1.5 }}>{translate('nav.myProfile')}</Box>
                            </MenuItem>
                            <Divider />
                            <ThemeModeSelector />
                            <Divider />
                            <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                                <LogOut size={15} />
                                <Box sx={{ ml: 1.5 }}>{translate('nav.logout')}</Box>
                            </MenuItem>
                        </Menu>
                    </Box>
                </Toolbar>
            </AppBar>
            <Drawer
                variant="temporary"
                open={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
                ModalProps={{ keepMounted: true }}
                sx={{
                    '& .MuiDrawer-paper': isMobile
                        ? { width: DRAWER_WIDTH, bgcolor: primaryMain, borderRight: 'none' }
                        : { width: DRAWER_WIDTH, bgcolor: primaryMain, borderRight: 'none', top: APPBAR_HEIGHT, height: `calc(100% - ${APPBAR_HEIGHT}px)` },
                    '& .MuiBackdrop-root': isMobile ? {} : { top: APPBAR_HEIGHT, bgcolor: 'rgba(0,0,0,0.3)' }
                }}
            >
                {renderDrawerContent()}
            </Drawer>
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    minWidth: 0,
                    minHeight: '100vh',
                    bgcolor: 'background.default'
                }}
            >
                <Toolbar variant="dense" sx={{ height: APPBAR_HEIGHT, minHeight: APPBAR_HEIGHT }} />
                <Outlet />
            </Box>
        </Box>
    );
}
